import * as Sentry from "@sentry/react";
import { createClient } from "@supabase/supabase-js";

import { STORAGE_KEY_WAITING_FOR_TOKEN } from "../../consts";
import { apiBase, supabaseAnonKey, supabaseUrl } from "../../environment";
import { clearAppTokens, setAppTokens } from "../../store/slices/AuthSlice";
import { ReduxStore } from "../../store/store.redux";

// ========================================
// Configuration
// ========================================

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

const originalFetch = globalThis.fetch;

// ========================================
// Token Management
// ========================================

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Extracts accountId from JWT access token
 */
const extractAccountIdFromToken = (token: string): string | null => {
	try {
		const payload = JSON.parse(atob(token.split(".")[1]));
		return payload.accountId || null;
	} catch {
		return null;
	}
};

/**
 * Gets current tokens from Redux store
 */
const getCurrentTokens = () => {
	const state = ReduxStore.getState();
	return {
		accessToken: state.auth.appJwt,
		refreshToken: state.auth.appRefreshToken,
	};
};

/**
 * Refreshes the TKA backend access token using the refresh token
 * Prevents concurrent refresh requests with mutex pattern
 */
const refreshAccessToken = async (): Promise<boolean> => {
	// Return existing refresh promise if already refreshing
	if (isRefreshing && refreshPromise) {
		return refreshPromise;
	}

	isRefreshing = true;
	refreshPromise = (async () => {
		try {
			const { accessToken, refreshToken } = getCurrentTokens();

			if (!refreshToken || !accessToken) {
				// eslint-disable-next-line no-console
				console.warn("[Token Refresh] No tokens available");
				return false;
			}

			const accountIdRaw = extractAccountIdFromToken(accessToken);
			if (!accountIdRaw) {
				// eslint-disable-next-line no-console
				console.warn("[Token Refresh] Could not extract accountId from token");
				return false;
			}

			// Normalize accountId: remove "Account:" prefix if present, then add it
			const accountId = accountIdRaw.startsWith("Account:")
				? accountIdRaw
				: `Account:${accountIdRaw}`;

			const response = await originalFetch(`${apiBase}/api/refresh-token`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					refreshToken,
					accountId,
				}),
			});

			if (!response.ok) {
				// eslint-disable-next-line no-console
				console.error("[Token Refresh] Failed:", response.status, response.statusText);
				return false;
			}

			const jsonData = await response.json();
			const data = jsonData as {
				accessToken: string;
				refreshToken: string;
			};
			if (data.accessToken && data.refreshToken) {
				ReduxStore.dispatch(
					setAppTokens({
						accessToken: data.accessToken,
						refreshToken: data.refreshToken,
					}),
				);
				return true;
			}

			// eslint-disable-next-line no-console
			console.warn("[Token Refresh] Response did not contain tokens");
			return false;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("[Token Refresh] Error:", error);
			return false;
		} finally {
			isRefreshing = false;
			refreshPromise = null;
		}
	})();

	return refreshPromise;
};

const MIN_REMAINING_VALIDITY_MS = 30000;

const expiresSoon = (token: string): boolean => {
	try {
		const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
		const { exp } = JSON.parse(atob(payload)) as { exp?: number };
		return !exp || exp * 1000 - Date.now() <= MIN_REMAINING_VALIDITY_MS;
	} catch {
		return true;
	}
};

export const getValidAppJwt = async (): Promise<string | undefined> => {
	const { accessToken } = getCurrentTokens();
	if (!accessToken) return undefined;
	if (!expiresSoon(accessToken)) return accessToken;

	const refreshed = await refreshAccessToken();
	return refreshed ? getCurrentTokens().accessToken ?? undefined : undefined;
};

// ========================================
// Request Helpers
// ========================================

/**
 * Extracts URL string from RequestInfo
 */
const getUrlFromRequest = (input: RequestInfo | URL): string => {
	if (typeof input === "string") return input;
	if (input instanceof URL) return input.toString();
	return input.url;
};

/**
 * Adds Supabase authentication header to request
 */
const addSupabaseAuthHeader = async (headers: Headers): Promise<void> => {
	const {
		data: { session },
	} = await supabase.auth.getSession();
	const supabaseToken = session?.access_token;

	if (supabaseToken) {
		headers.set("Authorization", `Bearer ${supabaseToken}`);
	}
};

/**
 * Adds TKA backend token to request
 */
const addTkaTokenHeader = (headers: Headers): void => {
	const { accessToken } = getCurrentTokens();
	if (accessToken) {
		headers.set("X-App-Authorization", `Bearer ${accessToken}`);
	}
};

/**
 * Creates a new request with updated headers
 */
const createRequestWithHeaders = (
	input: RequestInfo | URL,
	init: RequestInit | undefined,
	headers: Headers,
): Request | { input: RequestInfo | URL; init: RequestInit } => {
	if (input instanceof Request) {
		return new Request(input, { ...init, headers });
	}
	return { input, init: { ...init, headers } };
};

/**
 * Executes a fetch request with proper options
 */
const executeFetchRequest = async (
	input: RequestInfo | URL,
	init: RequestInit | undefined,
	headers: Headers,
): Promise<Response> => {
	const request = createRequestWithHeaders(input, init, headers);

	if (request instanceof Request) {
		return originalFetch(request);
	}

	return originalFetch(request.input, request.init);
};

/**
 * The backend auth failures the client branches on. `notVerifiable` is terminal: the Supabase token is not merely
 * stale, so no refresh can make it verifiable and the only way out of the broken session is a sign-out.
 */
type AuthFailureKind = "tokenExpired" | "notVerifiable" | null;

const SUPABASE_TOKEN_NOT_VERIFIABLE_CODE = "supabase_token_not_verifiable";
const SUPABASE_TOKEN_NOT_VERIFIABLE_MESSAGE = "supabase token could not be verified";

/**
 * Classifies an auth failure response.
 * Note: Supabase JWT refresh is handled automatically by SDK when getSession() is called
 */
const classifyAuthFailure = async (response: Response): Promise<AuthFailureKind> => {
	if (response.status !== 401 && response.status !== 403) {
		return null;
	}

	// Clone response before reading to avoid consuming the body
	const clonedResponse = response.clone();
	let errorMessage = "";
	try {
		const errorData = await clonedResponse.json();
		errorMessage = JSON.stringify(errorData).toLowerCase();
	} catch {
		return null;
	}

	// Match the stable machine-readable code first; the human-readable message stays as the fallback.
	if (
		errorMessage.includes(SUPABASE_TOKEN_NOT_VERIFIABLE_CODE) ||
		errorMessage.includes(SUPABASE_TOKEN_NOT_VERIFIABLE_MESSAGE)
	) {
		return "notVerifiable";
	}

	if (errorMessage.includes("jwt token expired") || errorMessage.includes("token expired")) {
		return "tokenExpired";
	}

	return null;
};

/**
 * Terminal auth failure: drop the local session and send the User to the login screen. Without it a User whose token
 * cannot be verified stays signed in with every request failing and no way to recover.
 */
export const signOutAndRedirectToLogin = async (): Promise<void> => {
	ReduxStore.dispatch(clearAppTokens());
	await supabase.auth.signOut();
	if (window.location.pathname !== "/login") {
		window.location.href = "/login";
	}
};

/**
 * Handles GraphQL requests with automatic token refresh on expiration
 */
const handleRequest = async (
	input: RequestInfo | URL,
	init: RequestInit | undefined,
): Promise<Response> => {
	const headers = new Headers(input instanceof Request ? input.headers : init?.headers);

	// Add authentication headers
	await addSupabaseAuthHeader(headers);
	addTkaTokenHeader(headers);

	// Make initial request
	let response = await executeFetchRequest(input, init, headers);

	// Check if token expired and retry if needed
	const authFailure = await classifyAuthFailure(response);
	if (authFailure === "notVerifiable") {
		// eslint-disable-next-line no-console
		console.error("[Fetch] Supabase token could not be verified - signing out");
		await signOutAndRedirectToLogin();
	} else if (authFailure === "tokenExpired") {
		const refreshed = await refreshAccessToken();

		if (refreshed) {
			// Update TKA token header
			const { accessToken } = getCurrentTokens();
			if (accessToken) {
				headers.set("X-App-Authorization", `Bearer ${accessToken}`);
			}

			// Refresh Supabase token header (SDK automatically refreshes if needed)
			await addSupabaseAuthHeader(headers);

			response = await executeFetchRequest(input, init, headers);
		} else {
			// eslint-disable-next-line no-console
			console.error(
				"[Fetch] Token refresh failed - clearing session and redirecting to login",
			);
			await signOutAndRedirectToLogin();
		}
	}

	return response;
};

/**
 * Guards /api/refresh-token endpoint from being called without tokens
 */
const handleRefreshTokenRequest = (): Response => {
	const { refreshToken } = getCurrentTokens();

	if (!refreshToken) {
		return new Response(
			JSON.stringify({
				error: "Backend tokens not yet generated",
			}),
			{
				status: 400,
				statusText: "Bad Request",
				headers: {
					"Content-Type": "application/json",
				},
			},
		);
	}

	// If tokens exist, let the request proceed normally
	throw new Error("Should not reach here - let request proceed");
};

// ========================================
// Fetch Interceptor
// ========================================

/**
 * Global fetch interceptor that handles:
 * - Adding authentication headers to GraphQL requests
 * - Automatic token refresh on expiration
 * - Guarding refresh token endpoint
 */
globalThis.fetch = (async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
	const url = getUrlFromRequest(input);

	// Intercept GraphQL requests for auth headers and token refresh
	if (url.includes("/api/graphql") || url.includes("/api/upload")) {
		return handleRequest(input, init);
	}

	// Guard refresh token endpoint
	if (url.includes("/api/refresh-token")) {
		try {
			return handleRefreshTokenRequest();
		} catch {
			// Let request proceed normally
		}
	}

	// Default: pass through to original fetch
	return originalFetch(input, init);
}) as typeof fetch;

// ========================================
// OAuth & Session Management
// ========================================

/**
 * Cleans OAuth tokens from URL hash after authentication
 */
const cleanOAuthHash = (): void => {
	if (window.location.hash.includes("access_token")) {
		window.history.replaceState(null, "", window.location.pathname + window.location.search);
	}
};

/**
 * Initializes Supabase session and cleans OAuth hash
 */
const initializeSession = async (): Promise<void> => {
	await supabase.auth.getSession();
	cleanOAuthHash();
};

/**
 * Sets up listener for Supabase auth state changes
 */
const setupAuthStateListener = (): void => {
	supabase.auth.onAuthStateChange((event, session) => {
		if (event === "SIGNED_OUT" || !session) {
			ReduxStore.dispatch(clearAppTokens());
			localStorage.removeItem(STORAGE_KEY_WAITING_FOR_TOKEN);
		}
		if (event === "SIGNED_IN") {
			cleanOAuthHash();
		}
	});
};

// ========================================
// Initialization
// ========================================

/**
 * Initializes the Supabase client and sets up auth listeners
 */
const initializeSupabase = (): void => {
	initializeSession().catch((error: unknown) => {
		if (error instanceof Error) {
			Sentry.captureException(error, { level: "warning" });
		}
	});
	setupAuthStateListener();
};

// Start initialization
initializeSupabase();
